import { useQuery } from 'react-query';
import axios from 'axios';
import { StaticTokens, TerminalAppearanceAPI } from '../../constants/API';
import { TerminalAppearanceResponse } from '../../types/Admin/TerminalAppearanceTypes';
import { baseSoundURL } from '../../config';

const normalizeMediaUrl = (url: string) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url) || url.startsWith('//')) {
    return url;
  }
  const separator = url.startsWith('/') ? '' : '/';
  return `${baseSoundURL}${separator}${url}`;
};

const normalizeButtonImageUrl = (url: string | null | undefined) => {
  if (!url) return url;
  return normalizeMediaUrl(url);
};

export const useGetTerminalAppearance = (branchQueueId?: number | null) => {
  const resolvedToken =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('token') || StaticTokens.terminalToken
      : StaticTokens.terminalToken;
  const isTokenAvailable = !!resolvedToken;
  const effectiveBranchQueueId = branchQueueId ?? null;
  const {
    data: terminalAppearanceResponse,
    isLoading: isLoadingTerminalAppearance,
    isSuccess: isTerminalAppearanceReceived
  } = useQuery(
    ['TerminalAppearance', resolvedToken, effectiveBranchQueueId],
    () =>
      axios.get<TerminalAppearanceResponse>(TerminalAppearanceAPI.getAppearance, {
        headers: {
          'X-API-TOKEN': resolvedToken
        },
        params: effectiveBranchQueueId != null ? { branchQueueId: effectiveBranchQueueId } : undefined
      }),
    {
      select: ({ data }) => ({
        ...data,
        settings: data.settings
          ? {
              ...data.settings,
              buttons: data.settings.buttons
                ? {
                    ...data.settings.buttons,
                    ticketOuterCircleSmallUrl: normalizeButtonImageUrl(
                      data.settings.buttons.ticketOuterCircleSmallUrl
                    )
                  }
                : data.settings.buttons,
              sectionButtons: data.settings.sectionButtons
                ? {
                    ...data.settings.sectionButtons,
                    ticketOuterCircleSmallUrl: normalizeButtonImageUrl(
                      data.settings.sectionButtons.ticketOuterCircleSmallUrl
                    )
                  }
                : data.settings.sectionButtons
            }
          : data.settings,
        backgrounds: (data.backgrounds || []).map((background) => ({
          ...background,
          url: normalizeMediaUrl(background.url)
        })),
        childrenBackgrounds: (data.childrenBackgrounds || []).map((background) => ({
          ...background,
          url: normalizeMediaUrl(background.url)
        }))
      }),
      enabled: isTokenAvailable,
      refetchInterval: 10000,
      notifyOnChangeProps: ['data', 'error']
    }
  );

  return { terminalAppearanceResponse, isLoadingTerminalAppearance, isTerminalAppearanceReceived };
};
